/**
 * Algolia Shared Utilities
 * Provides centralized userToken management to prevent duplicate Insights initialization,
 * and the one search event per finished query (reportSearch)
 *
 * This file MUST load before other Algolia scripts (algolia-collection-page.js, algolia-analytics.js)
 */

/**
 * Create global 'aa' function alias for Search Insights compatibility
 * The UMD bundle creates window.AlgoliaAnalytics.default but NOT window.aa
 * Many Algolia examples and our analytics code expect the 'aa' function to exist
 */
function createInsightsAlias() {
  if (typeof window.aa === 'function') return true;
  const insights = window.AlgoliaAnalytics?.default;
  if (!insights) return false;

  // Create 'aa' function that proxies to AlgoliaAnalytics.default methods
  window.aa = function (method, ...args) {
    if (typeof insights[method] === 'function') {
      return insights[method](...args);
    }
    console.warn('[Algolia] Unknown insights method:', method);
  };

  // InstantSearch's insights middleware reads the version to pick its API
  window.aa.version = insights.version;

  if (window.algoliaConfig?.debug) {
    console.log('[Algolia Shared] Created global aa() alias for Search Insights');
  }
  return true;
}

// search-insights.js is deferred after this file, so the alias can't exist
// yet. Page scripts that run later call AlgoliaShared.getInsightsClient(),
// which builds it on demand; this poll covers pages with no such script.
(function pollForInsights() {
  let attempts = 0;
  (function tryCreateAlias() {
    if (createInsightsAlias() || ++attempts >= 50) return;
    setTimeout(tryCreateAlias, 100);
  })();
})();

window.AlgoliaShared = window.AlgoliaShared || {
  // Single source of truth for userToken
  _userToken: null,
  _insightsInitialized: false,
  _isBot: null,
  _encoder: typeof TextEncoder !== 'undefined' ? new TextEncoder() : null,

  /**
   * Truncate a search query to fit within Algolia's 512-byte limit.
   * Uses byte length (not character count) so multi-byte characters
   * like emojis and CJK text are handled correctly.
   */
  truncateQuery(query, maxBytes = 500) {
    if (!query) return '';
    if (!this._encoder) return query.substring(0, maxBytes);
    const encoded = this._encoder.encode(query);
    if (encoded.length <= maxBytes) return query;
    // Decode only the first maxBytes bytes, replacing any truncated multi-byte char
    const decoder = new TextDecoder('utf-8', { fatal: false });
    return decoder.decode(encoded.slice(0, maxBytes)).replace(/\uFFFD$/, '');
  },

  /**
   * Detect if current visitor is likely a bot/crawler
   * Caches result to avoid repeated checks
   */
  isBot() {
    if (this._isBot !== null) {
      return this._isBot;
    }

    const ua = navigator.userAgent.toLowerCase();

    // Check for common bot patterns in user agent
    const botPatterns = [
      'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
      'yandexbot', 'sogou', 'exabot', 'facebot', 'facebookexternalhit',
      'ia_archiver', 'alexabot', 'mj12bot', 'ahrefsbot', 'semrushbot',
      'dotbot', 'rogerbot', 'screaming frog', 'gtmetrix', 'pingdom',
      'pagespeed', 'lighthouse', 'chrome-lighthouse', 'headlesschrome',
      'phantomjs', 'prerender', 'crawler', 'spider', 'bot/', '/bot',
      'applebot', 'twitterbot', 'linkedinbot', 'slackbot', 'whatsapp',
      'telegrambot', 'discordbot', 'uptimerobot', 'statuscake'
    ];

    const isUaBot = botPatterns.some(pattern => ua.includes(pattern));

    // Check for headless browser indicators
    const isHeadless = (
      navigator.webdriver === true ||
      window.navigator.userAgent.includes('HeadlessChrome') ||
      window.Cypress !== undefined ||
      window.__nightmare !== undefined ||
      window.callPhantom !== undefined ||
      window._phantom !== undefined
    );

    // Check for missing browser features that bots often lack. Not
    // window.chrome: Android WebView (Instagram and Facebook in-app browsers)
    // has "Chrome" in its UA and no window.chrome.
    const lacksFeatures = (
      navigator.languages === undefined ||
      navigator.languages?.length === 0
    );

    this._isBot = isUaBot || isHeadless || lacksFeatures;

    if (this._isBot && window.algoliaConfig?.debug) {
      console.log('[Algolia Shared] Bot detected, skipping analytics');
    }

    return this._isBot;
  },

  /**
   * Get or create a consistent userToken across all Algolia components
   * Priority: 1) Customer ID from algoliaConfig (stable, set in Liquid), 2) localStorage token
   * Logged-in customers use "cust_{id}" which follows them across devices/sessions
   */
  getUserToken() {
    if (this._userToken) {
      return this._userToken;
    }

    // Priority 1: Customer ID from algoliaConfig (set in Liquid)
    // Format: "cust_12345678" - stable across email changes, follows user cross-device
    if (window.algoliaConfig?.userToken) {
      this._userToken = window.algoliaConfig.userToken;
      // Persist as cookie so the Web Pixel (sandboxed) can read it
      try {
        const exp = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = 'algolia_user_token=' + encodeURIComponent(this._userToken) + '; expires=' + exp + '; path=/; SameSite=Lax; Secure';
      } catch (_e) {
        // Cookie not available
      }
      return this._userToken;
    }

    // Priority 2: Anonymous users get localStorage-based token. localStorage is null in some
    // sandboxed contexts and getItem/setItem can throw under Safari private mode + storage
    // pressure. SHOPIFY-1Y8/1Y9 fired here. Generate a fresh token if storage is unavailable
    // so Algolia events still get a consistent userToken for the lifetime of the page; without
    // persistence the user just gets a new token next page load.
    const storageKey = 'algolia_user_token';
    let token = null;
    try {
      if (window.localStorage) {
        token = localStorage.getItem(storageKey);
      }
    } catch (_e) {
      /* storage blocked */
    }

    if (!token) {
      // Generate new token with consistent format
      token = 'user_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      try {
        if (window.localStorage) {
          localStorage.setItem(storageKey, token);
        }
      } catch (_e) {
        /* quota exceeded or storage blocked — non-persistent token still works for this page */
      }
    }

    this._userToken = token;

    // Persist as cookie so the Web Pixel (sandboxed) can read it
    try {
      const exp = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = storageKey + '=' + encodeURIComponent(token) + '; expires=' + exp + '; path=/; SameSite=Lax; Secure';
    } catch (_e) {
      // Cookie not available
    }

    return this._userToken;
  },

  /**
   * The insightsClient to hand InstantSearch: the global aa() alias, built
   * now if search-insights.js has run, or null if it hasn't loaded.
   */
  getInsightsClient() {
    return createInsightsAlias() ? window.aa : null;
  },

  /**
   * Initialize Algolia Insights once, shared across all components
   * Returns true if this call initialized Insights, false if already initialized
   * Bots get an opted-out client, so their events are dropped and calls don't throw
   */
  initInsights(config) {
    if (this._insightsInitialized) {
      return false;
    }

    if (!config?.appId || !config?.apiKey) {
      console.warn('[Algolia Shared] Cannot init Insights: missing credentials');
      return false;
    }

    const insights = window.AlgoliaAnalytics?.default;
    if (!insights) {
      return false;
    }

    insights.init({
      appId: config.appId,
      apiKey: config.apiKey,
      useCookie: false,
      userHasOptedOut: !!window.lampsPreviewBuild || this.isBot()
    });
    insights.setUserToken(this.getUserToken());
    createInsightsAlias();
    this._insightsInitialized = true;
    return true;
  },

  /**
   * Check if Insights has been initialized
   */
  isInsightsInitialized() {
    return this._insightsInitialized;
  },

  /**
   * Mark Insights as initialized (for components that init it themselves)
   */
  markInsightsInitialized() {
    this._insightsInitialized = true;
  },

  // ============================================
  // Search events
  // ============================================

  /** How long a query has to hold before a results page counts it as a search */
  SEARCH_SETTLE_MS: 1000,

  _pendingSearch: null,
  _searchTimer: null,
  _lastSearchKey: null,
  _searchRedirecting: false,

  /**
   * Results pages call this on every render. Search-as-you-type renders on
   * each keystroke, so the query counts once it has held for SEARCH_SETTLE_MS,
   * or as soon as the shopper follows a link or leaves the page. Term and count
   * both come from `results`, the response being rendered. Filters and pages on
   * the same query record nothing more.
   * @param {Object} results - InstantSearch SearchResults for the main index
   * @param {string} surface - "search" or "collection"
   */
  observeSearch(results, surface) {
    if (this._searchRedirecting || !results) return;
    clearTimeout(this._searchTimer);
    const query = (results.query || '').trim();
    if (!query) {
      this._pendingSearch = null;
      return;
    }
    this._pendingSearch = { query, resultsCount: results.nbHits, surface };
    this._searchTimer = setTimeout(() => this.flushSearch(), this.SEARCH_SETTLE_MS);
  },

  /** Record the query still settling, if there is one. */
  flushSearch() {
    clearTimeout(this._searchTimer);
    const pending = this._pendingSearch;
    this._pendingSearch = null;
    if (pending) this.reportSearch(pending);
  },

  /**
   * Record a finished search, once per query per page. Sends GA4's `search`
   * event (PostHog forwards it from dataLayer) and appends to
   * window.lampsSearches, which Klaviyo replays when its tracking starts.
   * Following a redirect is its own record: on a collection page the shopper
   * can see a query's results and then press Enter to take its redirect.
   * @param {{query: string, resultsCount: number, surface: string, redirected?: boolean}} search
   */
  reportSearch(search) {
    const query = (search.query || '').trim();
    if (!query) return;
    const key = `${query.toLowerCase()}|${!!search.redirected}`;
    if (key === this._lastSearchKey) return;
    this._lastSearchKey = key;

    const entry = {
      query,
      resultsCount: search.resultsCount,
      surface: search.surface,
      redirected: !!search.redirected,
    };

    if (typeof window.gtag === 'function') {
      const params = {
        search_term: query,
        results_count: entry.resultsCount,
        surface: entry.surface,
        event_category: 'Algolia Search',
      };
      if (entry.redirected) params.redirected = true;
      window.gtag('event', 'search', params);
    }

    (window.lampsSearches = window.lampsSearches || []).push(entry);
    document.dispatchEvent(new CustomEvent('lamps:search', { detail: entry }));
  },

  /**
   * A redirect rule matched a submitted query and the page is about to
   * navigate. Records it as a redirected search with the real result count,
   * and ignores the emptied render that follows.
   */
  reportRedirect(query, resultsCount, surface) {
    clearTimeout(this._searchTimer);
    this._pendingSearch = null;
    this._searchRedirecting = true;
    this.reportSearch({ query, resultsCount, surface, redirected: true });
  },

  // ============================================
  // Constants
  // ============================================

  /** Max retry attempts for waiting on async resources (e.g., Insights library) */
  MAX_RETRY_ATTEMPTS: 15,

  /** Delay between retry attempts in milliseconds */
  RETRY_DELAY_MS: 200,

  /** Maximum items to store in conversion data array */
  MAX_CONVERSION_ITEMS: 20,

  /** Conversion data expiry in days */
  CONVERSION_EXPIRY_DAYS: 7,

  /** Attribution session timeout in minutes */
  ATTRIBUTION_TIMEOUT_MINUTES: 30,

  // ============================================
  // Shared Utilities
  // ============================================

  /**
   * Convert Algolia helper state filters to a compact string
   * @param {Object} state - Algolia helper state
   * @returns {string} - Filters as string (e.g., "brand:Acme,color:Red")
   */
  getFiltersString(state) {
    if (!state) return '';

    const parts = [];

    // Disjunctive facets (OR within facet)
    const disjunctive = state.disjunctiveFacetsRefinements || {};
    for (const [facet, values] of Object.entries(disjunctive)) {
      if (values && values.length > 0) {
        parts.push(`${facet}:${values.join('|')}`);
      }
    }

    // Conjunctive facets (AND within facet)
    const conjunctive = state.facetsRefinements || {};
    for (const [facet, values] of Object.entries(conjunctive)) {
      if (values && values.length > 0) {
        parts.push(`${facet}:${values.join('&')}`);
      }
    }

    // Numeric refinements
    const numeric = state.numericRefinements || {};
    for (const [facet, operators] of Object.entries(numeric)) {
      for (const [op, opValues] of Object.entries(operators || {})) {
        if (opValues && opValues.length > 0) {
          parts.push(`${facet}${op}${opValues.join(',')}`);
        }
      }
    }

    return parts.join(',');
  },

  /**
   * Keep products tagged `unlisted` (#1885) or `merch-hidden` (#1951) out of
   * storefront search and Algolia grids. Both stay active in Shopify so their
   * URL works, which means the Algolia integration indexes them like any
   * other product. House tags every product of a Hidden merch-tier family
   * `merch-hidden`.
   *
   * Wraps an algoliasearch v4 client so every request to the products index
   * or one of its sort replicas carries `NOT tags:unlisted AND NOT
   * tags:merch-hidden`, ANDed with the request's own filters. Collections,
   * pages and query-suggestion requests pass through untouched. Requires `tags` to be filterOnly on the index;
   * until then the clause matches every record.
   *
   * @param {Object} client - algoliasearch client
   * @returns {Object} client with search, searchForFacetValues and initIndex wrapped
   */
  excludeUnlisted(client) {
    const shared = this;
    const withFilter = (indexName, params) =>
      shared.isProductsIndex(indexName)
        ? { ...params, filters: shared.withHiddenFilter(params?.filters) }
        : params;
    const wrapQueries = (queries) =>
      queries.map((q) => ({ ...q, params: withFilter(q.indexName, q.params) }));

    return {
      ...client,
      search(queries, requestOptions) {
        return client.search(wrapQueries(queries), requestOptions);
      },
      searchForFacetValues(queries, requestOptions) {
        return client.searchForFacetValues(wrapQueries(queries), requestOptions);
      },
      initIndex(indexName) {
        const index = client.initIndex(indexName);
        if (!shared.isProductsIndex(indexName)) return index;
        return {
          ...index,
          search(query, requestOptions) {
            return index.search(query, withFilter(indexName, requestOptions));
          },
          searchForFacetValues(facetName, facetQuery, requestOptions) {
            return index.searchForFacetValues(facetName, facetQuery, withFilter(indexName, requestOptions));
          },
        };
      },
    };
  },

  /**
   * The products index plus anything named `<products index>_*`: the sort
   * replicas, and _bestsellers, which the popular-products fallback queries
   * whether or not it is in sortIndices. The other configured indexes are
   * excluded by name, since query suggestions shares the prefix.
   */
  isProductsIndex(indexName) {
    const config = window.algoliaConfig;
    const base = config?.indexName;
    if (!base || !indexName) return false;
    if ([config.suggestionsIndex, config.collectionsIndex, config.pagesIndex].includes(indexName)) {
      return false;
    }
    return indexName === base || indexName.startsWith(`${base}_`);
  },

  /**
   * AND `NOT tags:unlisted AND NOT tags:merch-hidden` onto an Algolia filters
   * string. The existing filters are parenthesised so a bare `a OR b` keeps
   * its meaning.
   */
  withHiddenFilter(filters) {
    const clause = 'NOT tags:unlisted AND NOT tags:merch-hidden';
    return filters && filters.trim() ? `(${filters}) AND ${clause}` : clause;
  },

  /**
   * Back, forward and reload put the browser's scroll position over the
   * server-rendered grid, which isn't the grid Algolia draws: Shopify can't
   * apply Algolia's filters, so a filtered ?page=N URL renders a different
   * set of products. When Algolia swaps its grid in, everything under the
   * fold moves (shop-theme#2002). Turn off the browser's restore, keep the
   * position per URL on pagehide, and put it back from restoreScroll() once
   * Algolia's grid is in.
   */
  manageScrollRestoration() {
    if (!('scrollRestoration' in history) || this._scrollTarget !== undefined) return;
    history.scrollRestoration = 'manual';
    const key = 'algolia:scroll';
    const read = () => {
      try { return JSON.parse(sessionStorage.getItem(key)) || {}; } catch { return {}; }
    };
    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    const returning = navigation && (navigation.type === 'back_forward' || navigation.type === 'reload');
    this._scrollTarget = returning ? read()[location.href] || 0 : 0;
    // A fallback, in case Algolia never renders.
    if (this._scrollTarget) setTimeout(() => this.restoreScroll(), 5000);
    window.addEventListener('pagehide', () => {
      const positions = read();
      delete positions[location.href];
      positions[location.href] = Math.round(window.scrollY);
      const recent = Object.entries(positions).slice(-20);
      try { sessionStorage.setItem(key, JSON.stringify(Object.fromEntries(recent))); } catch { /* Optional storage. */ }
    });
  },

  /**
   * Scroll back to where the shopper left this URL, once, after Algolia's
   * first render. swapGridContent pins the grid's height until its last
   * chunk of cards is in, so this waits for the pin to go. Does nothing if
   * the shopper has already scrolled.
   */
  restoreScroll(grid) {
    const target = this._scrollTarget;
    if (!target) return;
    this._scrollTarget = 0;
    let frames = 0;
    const attempt = () => {
      if (grid?.style.minHeight && ++frames < 60) {
        requestAnimationFrame(attempt);
        return;
      }
      requestAnimationFrame(() => {
        if (window.scrollY === 0) window.scrollTo({ top: target, behavior: 'auto' });
      });
    };
    requestAnimationFrame(attempt);
  },

  /**
   * Sanitize a string value for safe storage/transmission
   * Removes potentially dangerous characters while preserving useful data
   * @param {string} value - The value to sanitize
   * @param {number} maxLength - Maximum allowed length (default 500)
   * @returns {string} - Sanitized string
   */
  sanitizeValue(value, maxLength = 500) {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // Remove control characters and limit length
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x1F\x7F]/g, '').substring(0, maxLength);
  },

  /**
   * Rewrite the collection-schema JSON-LD script with an ItemList built
   * from the current Algolia hits. Mirrors the SSR shape emitted by
   * theme/snippets/collection-schema.liquid so faceted / sorted /
   * search views get an accurate schema (Liquid SSR's `collection.products`
   * doesn't reflect Algolia's reorder).
   *
   * No-op if the script tag isn't on the page (template doesn't render it).
   *
   * @param {Array<Object>} hits - Algolia hits for the current render.
   */
  updateCollectionSchema(hits) {
    if (!Array.isArray(hits)) return;
    const script = document.querySelector('script[data-collection-schema]');
    if (!script) return;

    const origin = window.location.origin;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    // Cap mirrors collection-schema.liquid. Reduced from 60 to 20 on
    // 2026-05-06 (Pathway 6 of the CWV pathways audit) — JSON-LD product
    // loop was 65 ms / 8.1% of the 808 ms collection-page Liquid VM render.
    // 20 is enough for Google's collection-page structured data; Search
    // Console picks up the full catalog via XML sitemaps. SSR + post-Algolia
    // render shapes must stay aligned, so this cap and collection-schema.liquid
    // must move together.
    const MAX_ITEMS = 20;
    const items = [];

    for (let i = 0; i < hits.length && items.length < MAX_ITEMS; i++) {
      const hit = hits[i];
      if (!hit || !hit.handle) continue;

      const priceMin = hit.variants_min_price ?? hit.price_min ?? hit.price;
      const priceMax = hit.variants_max_price ?? hit.price_max ?? hit.price ?? priceMin;
      if (priceMin === undefined || priceMin === null) continue;

      // Match the dual-check pattern used in algolia-search-page.js:2843 —
      // `inventory_available` is the Shopify Algolia integration's canonical
      // OOS facet; bare `available` is not a documented hit field. Using
      // only `available` would mark OOS products as InStock when Algolia
      // omits it, disagreeing with SSR Liquid's `product.available`.
      const available = hit.inventory_available !== false && hit.available !== false;

      const rawImage = hit.image || hit.featured_image || (hit.images && hit.images[0]) || null;
      // Match SSR's `image_url: width: 600` — Shopify CDN URLs accept a
      // _<size>x suffix before the extension. Keeps schema consistent
      // across SSR and post-Algolia rewrite for Google dedup.
      const image = rawImage && /cdn\.shopify\.com/.test(rawImage)
        ? rawImage.replace(/(\.(?:jpe?g|png|webp|gif))(\?.*)?$/i, '_600x$1$2')
        : rawImage;

      const product = {
        '@type': 'Product',
        name: String(hit.title || ''),
        url: `${origin}/products/${hit.handle}`,
      };
      if (image) product.image = String(image);
      if (hit.vendor) product.brand = { '@type': 'Brand', name: String(hit.vendor) };
      product.offers = {
        '@type': 'AggregateOffer',
        priceCurrency: currency,
        lowPrice: Number(priceMin),
        highPrice: Number(priceMax ?? priceMin),
        offerCount: Number(hit.variant_count || hit.variants_count || 1),
        availability: available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      };

      items.push({
        '@type': 'ListItem',
        position: items.length + 1,
        item: product,
      });
    }

    const payload = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items,
    };
    if (script.dataset.itemListId) payload['@id'] = script.dataset.itemListId;

    try {
      script.textContent = JSON.stringify(payload);
    } catch (e) {
      console.warn('[AlgoliaShared] updateCollectionSchema serialize failed', e);
    }
  }
};

// A query still settling has finished once the shopper follows a link or
// leaves; the settle timer would otherwise end with the page.
document.addEventListener('click', (e) => {
  if (e.target instanceof Element && e.target.closest('a[href]')) window.AlgoliaShared.flushSearch();
}, true);
window.addEventListener('pagehide', () => window.AlgoliaShared.flushSearch());
// Back from a redirect can restore this page from the back/forward cache, where
// the shopper can search again
window.addEventListener('pageshow', (e) => {
  if (e.persisted) window.AlgoliaShared._searchRedirecting = false;
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') window.AlgoliaShared.flushSearch();
});
